<script setup lang="ts">
import { useClassStore, useSessionStore, useSignalRStore, useUserStore } from '../../stores';
import Heading from '../General/Heading.vue';
import Button from '../General/Button.vue';
import { useI18n } from 'petite-vue-i18n';
import { TEXT } from '../../configs/text';
import { computed, ref } from 'vue';
import { ClassStatus, classStatusMap } from '../../types/class';
import { UserRole } from '../../types/user';
import { storeToRefs } from 'pinia';
import Modal from '../General/Modal.vue';
import { utils } from '../../utils';
import arrowIcon from '../../assets/icons/arrow.svg';

const classStore = useClassStore()
const sessionStore = useSessionStore();
const user = useUserStore()
const signalr = useSignalRStore()
const { selectedClassDetails: classDetails } = storeToRefs(classStore)
const statusName = computed<string>(() => classDetails.value?.state ? classStatusMap[classDetails.value?.state] : '')
const isClassStartable = computed<boolean>(() => {
    if (!classDetails.value?.start_date || !classDetails.value?.start_time) return false;
    if (!classDetails.value?.end_date || !classDetails.value?.end_time) return false;

    const allowedTimestamp = 30 * 60 * 1000 // 30mins
    const startTimestamp = classDetails.value.start_date * 1000 + utils.timeStringToMilliseconds(classDetails.value.start_time)
    const endTimestamp = classDetails.value.end_date * 1000 + utils.timeStringToMilliseconds(classDetails.value.end_time)
    const currentTimestamp = Date.now()

    return classDetails.value?.state === ClassStatus.not_started
        && currentTimestamp > (startTimestamp - allowedTimestamp)
        && currentTimestamp < endTimestamp
})

const modalShow = ref<boolean>(false)

function handleStartClass() {
    modalShow.value = true
}

function handleStartClassConfirm() {
    modalShow.value = false
    signalr.startClassRoom()
}

function handleClearClass() {
    classStore.clearClassInfo()
}
function classDetailToggle() {
    classStore.isClassDetailCollapsed = !classStore.isClassDetailCollapsed;
}

const { t } = useI18n()
</script>

<template>
    <div class="bg-white rounded-lg p-4 pb-1 md:p-8 md:pb-2 mb-5">
        <div class="flex flex-wrap items-start w-full gap-3">
            <div class="md:shrink-0">
                <Heading color="main" size="large">{{ classDetails?.name }}</Heading>
                <template v-if="!classStore.isClassDetailCollapsed">
                    <div class="mb-1">
                        {{ t(TEXT.schedule) }}:
                        <strong>{{ classStore.getClassScheduleFormat(classDetails) }}</strong>
                        <span class="text-neutral-400 ml-2">{{ t(statusName) }}</span>
                    </div>
                    <div class="mb-1">
                        {{ t(TEXT.teacher) }}: <strong>{{ classDetails?.teacher_name }}</strong>
                    </div>
                    <div v-if="classDetails?.assistant_name">
                        {{ t(TEXT.teacher_assistant) }}: <strong>{{ classDetails?.assistant_name }}</strong>
                    </div>
                </template>
            </div>
            <template v-if="user.userRole === UserRole.teacher">
                <div class="flex gap-3 ml-auto">
                    <Button color="warning" @click="handleClearClass">
                        {{ t(TEXT.back_to_schedule) }}
                    </Button>
                    <Button v-if="isClassStartable" color="success" @click="handleStartClass">
                        {{ t(TEXT.start) }}
                    </Button>
                    <Modal v-model="modalShow">
                        <template v-if="!sessionStore.classContent">
                            <Heading alignment="center">{{ t(TEXT.no_lesson_content) }}</Heading>
                            <div class="text-center" v-html="t(TEXT.no_lesson_content_desc)"></div>
                        </template>
                        <template v-else-if="!classStore.attendedStudentCount">
                            <Heading alignment="center">{{ t(TEXT.no_student_joined) }}</Heading>
                            <div class="text-center" v-html="t(TEXT.no_student_joined_desc)"></div>
                        </template>
                        <template v-else>
                            <Heading alignment="center">{{ t(TEXT.start_iclass) }}</Heading>
                            <div class="text-center">{{ t(TEXT.are_you_sure_to_start_class) }}</div>
                            <div class="text-center">
                                {{
                                    t(TEXT.student_joined_in_class, {
                                        attended_students: classStore.attendedStudentCount,
                                        total_students: classDetails?.students?.length || 0
                                    })
                                }}
                            </div>
                            <Button color="success" class="mt-5 mx-auto" @click="handleStartClassConfirm">
                                {{ t(TEXT.start) }}
                            </Button>
                        </template>
                    </Modal>
                </div>
            </template>
        </div>
        <div class="flex justify-center mt-3">
            <img :src="arrowIcon" @click="classDetailToggle" class="cursor-pointer"
                :class="classStore.isClassDetailCollapsed ? 'rotate-up' : 'rotate-down'" />
        </div>
    </div>
</template>
<style scoped>
@keyframes rotateDown {
    from {
        transform: rotateX(0deg);
    }

    to {
        transform: rotateX(180deg);
    }
}

@keyframes rotateUp {
    from {
        transform: rotateX(180deg);
    }

    to {
        transform: rotateX(0deg);
    }
}

.rotate-down {
    animation: rotateDown .2s ease 0s 1 reverse forwards;
}

.rotate-up {
    animation: rotateUp .2s ease 0s 1 reverse forwards;
}
</style>